// ParentsDashboard.jsx - Premium Blue & White Sidebar Design
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSchoolName } from "./schoolNameStore";

// ─────────────────────────────────────────────────────────────────────────────
//  SUBJECT COLORS — BLUE THEME (PRESERVED)
// ─────────────────────────────────────────────────────────────────────────────
import { AlertTriangle, ArrowRight } from "lucide-react";
const subjectColors = {
  Tamil: "#0D47A1",
  English: "#006994",
  Maths: "#1976D2",
  Mathematics: "#1976D2",
  Science: "#0D47A1",
  History: "#0288D1",
  "P.E": "#0D47A1",
  "Physical Education": "#0D47A1",
  Computer: "#006994",
  "Computer Science": "#006994",
  Art: "#0D47A1",
  "Art & Craft": "#0D47A1",
  "Social Science": "#0D47A1",
  "Moral Science": "#1976D2",
  Library: "#0288D1",
  Sports: "#0D47A1",
  "Value Education": "#1976D2",
  "Free Period": "#0D47A1",
  Assembly: "#1976D2",
  Games: "#0D47A1",
  "EVS (Environmental Studies)": "#0D47A1",
  "Body Parts": "#006994",
  "Drawing / Coloring": "#0D47A1",
  "Drawing / Coloring / Craft": "#0D47A1",
  "Rhymes & Storytelling": "#1976D2",
  "Activity / Play Time": "#0D47A1",
  "GK (General Knowledge)": "#006994",
  "Dance / Music / Yoga": "#0D47A1",
  "Music / Dance / Yoga / Games": "#0D47A1",
  "Handwriting Practice": "#1976D2",
  Handwriting: "#1976D2",
  "Computer Basics": "#006994",
  "Computer Applications": "#006994",
  Physics: "#0D47A1",
  Chemistry: "#1976D2",
  Biology: "#006994",
  Geography: "#0D47A1",
  Economics: "#0D47A1",
  Commerce: "#1976D2",
  Accountancy: "#1976D2",
  "Business Maths": "#0D47A1",
  "Political Science": "#006994",
  Sociology: "#0D47A1",
  French: "#0288D1"
};
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const PERIODS = [{
  id: 1,
  label: "Period 1",
  time: "8:30–9:10"
}, {
  id: 2,
  label: "Period 2",
  time: "9:10–9:50"
}, {
  id: 3,
  label: "Period 3",
  time: "9:50–10:30"
}, {
  id: 4,
  label: "Break",
  time: "10:30–10:45",
  isBreak: true
}, {
  id: 5,
  label: "Period 4",
  time: "10:45–11:25"
}, {
  id: 6,
  label: "Period 5",
  time: "11:25–12:05"
}, {
  id: 7,
  label: "Lunch",
  time: "12:05–12:45",
  isBreak: true
}, {
  id: 8,
  label: "Period 6",
  time: "12:45–1:25"
}, {
  id: 9,
  label: "Period 7",
  time: "1:25–2:05"
}, {
  id: 10,
  label: "Period 8",
  time: "2:05–2:45"
}];
const initialFees = {
  total: 0,
  paid: 0,
  balance: 0,
  history: []
};
const emptyComponents = () => ({
  schoolFee: 0,
  vanFee: 0,
  bookFee: 0,
  tuitionFee: 0,
  others: []
});
const initialTermFees = {
  T1: {
    due: 0,
    paid: 0,
    pending: 0,
    components: emptyComponents(),
    componentsPaid: emptyComponents()
  },
  T2: {
    due: 0,
    paid: 0,
    pending: 0,
    components: emptyComponents(),
    componentsPaid: emptyComponents()
  },
  T3: {
    due: 0,
    paid: 0,
    pending: 0,
    components: emptyComponents(),
    componentsPaid: emptyComponents()
  }
};
function parseClassSection(className) {
  if (!className) return {
    dbClassName: "",
    section: ""
  };
  const lastDash = className.lastIndexOf("-");
  if (lastDash === -1) return {
    dbClassName: className,
    section: ""
  };
  return {
    dbClassName: className.slice(0, lastDash).trim(),
    section: className.slice(lastDash + 1).trim()
  };
}
function academicYearStart() {
  const now = new Date();
  const year = now.getMonth() >= 5 ? now.getFullYear() : now.getFullYear() - 1;
  return `${year}-06-01`;
}
function todayIST() {
  return new Date(Date.now() + 5.5 * 3600000).toISOString().slice(0, 10);
}

// ─────────────────────────────────────────────────────────────────────────────
//  PREMIUM BLUE & WHITE SIDEBAR CSS — Unique, Clean, Classic
// ─────────────────────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap');
  @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css');

  *, *::before, *::after {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Inter', sans-serif;
    background: #E3F2FD;
    color: #0D2436;
    line-height: 1.6;
  }

  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: #E3F2FD; border-radius: 10px; }
  ::-webkit-scrollbar-thumb { background: #0D47A1; border-radius: 10px; }

  /* ── Sidebar Layout ── */
  .pd-app {
    display: flex;
    min-height: 100vh;
  }

  /* ── Sidebar — Pure Blue & White ── */
  .pd-sidebar {
    width: 260px;
    min-height: 100vh;
    background: #0D47A1;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 100;
    display: flex;
    flex-direction: column;
    box-shadow: 4px 0 30px rgba(13, 71, 161, 0.2);
    transition: all 0.3s ease;
  }

  .pd-sidebar-brand {
    padding: 28px 24px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
  }
  .pd-sidebar-logo {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .pd-sidebar-icon {
    width: 44px;
    height: 44px;
    background: rgba(255,255,255,0.12);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #FFFFFF;
    font-size: 20px;
    border: 1px solid rgba(255,255,255,0.1);
  }
  .pd-sidebar-title {
    font-family: 'Playfair Display', serif;
    font-weight: 700;
    font-size: 20px;
    color: #FFFFFF;
    letter-spacing: 0.5px;
  }
  .pd-sidebar-sub {
    font-size: 9px;
    font-weight: 400;
    color: rgba(255,255,255,0.5);
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-top: 2px;
  }

  .pd-sidebar-user {
    padding: 20px 24px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .pd-sidebar-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #90CAF9;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 18px;
    color: #0D47A1;
    flex-shrink: 0;
  }
  .pd-sidebar-user-name {
    font-weight: 600;
    font-size: 14px;
    color: #FFFFFF;
  }
  .pd-sidebar-user-role {
    font-size: 10px;
    color: rgba(255,255,255,0.5);
  }

  .pd-sidebar-nav {
    flex: 1;
    padding: 16px 12px 20px;
    overflow-y: auto;
  }
  .pd-sidebar-nav-label {
    font-size: 9px;
    font-weight: 600;
    color: rgba(255,255,255,0.25);
    text-transform: uppercase;
    letter-spacing: 1.5px;
    padding: 8px 12px 12px;
  }
  .pd-sidebar-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 11px 16px;
    border-radius: 10px;
    color: rgba(255,255,255,0.6);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-bottom: 2px;
    text-decoration: none;
    border: none;
    background: transparent;
    width: 100%;
  }
  .pd-sidebar-item i {
    width: 20px;
    font-size: 15px;
    text-align: center;
  }
  .pd-sidebar-item:hover {
    background: rgba(255,255,255,0.1);
    color: #FFFFFF;
  }
  .pd-sidebar-item.active {
    background: #FFFFFF;
    color: #0D47A1;
    box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  }
  .pd-sidebar-item.active i {
    color: #0D47A1;
  }
  .pd-sidebar-item .badge {
    margin-left: auto;
    background: #2196F3;
    color: #FFFFFF;
    font-size: 10px;
    padding: 2px 10px;
    border-radius: 40px;
  }

  .pd-sidebar-footer {
    padding: 16px 24px 24px;
    border-top: 1px solid rgba(255,255,255,0.08);
  }
  .pd-sidebar-logout {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    border-radius: 10px;
    color: rgba(255,255,255,0.5);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 1px solid rgba(255,255,255,0.06);
    background: transparent;
    width: 100%;
  }
  .pd-sidebar-logout:hover {
    background: rgba(255,0,0,0.1);
    color: #FF6B6B;
    border-color: rgba(255,0,0,0.15);
  }
  .pd-sidebar-logout i {
    width: 20px;
  }

  /* ── Main Content ── */
  .pd-main {
    flex: 1;
    margin-left: 260px;
    padding: 24px 32px 40px;
    min-height: 100vh;
    background: #E3F2FD;
  }

  /* ── Top Bar ── */
  .pd-topbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(13, 71, 161, 0.08);
  }
  .pd-topbar-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .pd-hamburger {
    display: none;
    background: none;
    border: none;
    color: #0D47A1;
    font-size: 22px;
    cursor: pointer;
    padding: 4px;
  }
  .pd-hamburger:hover {
    color: #2196F3;
  }
  .pd-topbar-title {
    font-family: 'Playfair Display', serif;
    font-weight: 700;
    font-size: 22px;
    color: #0D2436;
  }
  .pd-topbar-title span {
    color: #2196F3;
  }
  .pd-topbar-date {
    font-size: 13px;
    color: #4A6D8C;
    background: #FFFFFF;
    padding: 6px 16px;
    border-radius: 40px;
    border: 1px solid rgba(13, 71, 161, 0.06);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* ── Hero Card ── */
  .pd-hero {
    background: #FFFFFF;
    border-radius: 20px;
    padding: 24px 32px;
    box-shadow: 0 4px 24px rgba(13, 71, 161, 0.06);
    border: 1px solid rgba(13, 71, 161, 0.06);
    display: flex;
    align-items: center;
    gap: 24px;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }
  .pd-hero-photo {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: #E3F2FD;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    color: #0D47A1;
    border: 3px solid #90CAF9;
    flex-shrink: 0;
    overflow: hidden;
  }
  .pd-hero-info {
    flex: 1;
  }
  .pd-hero-name {
    font-family: 'Playfair Display', serif;
    font-weight: 700;
    font-size: 22px;
    color: #0D2436;
  }
  .pd-hero-meta {
    display: flex;
    gap: 18px;
    margin-top: 4px;
    font-size: 13px;
    color: #4A6D8C;
    flex-wrap: wrap;
  }
  .pd-hero-meta span {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .pd-hero-meta i {
    color: #0D47A1;
  }
  .pd-hero-stats {
    display: flex;
    gap: 28px;
    margin-left: auto;
  }
  .pd-hero-stat {
    text-align: center;
  }
  .pd-hero-stat-value {
    font-weight: 700;
    font-size: 24px;
    color: #0D47A1;
  }
  .pd-hero-stat-label {
    font-size: 10px;
    font-weight: 500;
    color: #4A6D8C;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  /* ── Content Area ── */
  .pd-content {
    background: #FFFFFF;
    border-radius: 20px;
    padding: 24px 28px;
    box-shadow: 0 4px 24px rgba(13, 71, 161, 0.06);
    border: 1px solid rgba(13, 71, 161, 0.06);
    min-height: 400px;
  }
  .pd-content-title {
    font-family: 'Playfair Display', serif;
    font-weight: 600;
    font-size: 18px;
    color: #0D2436;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .pd-content-title i {
    color: #0D47A1;
  }

  /* ── Cards ── */
  .pd-card {
    background: #E3F2FD;
    border-radius: 16px;
    padding: 20px;
    border: 1px solid rgba(13, 71, 161, 0.06);
  }
  .pd-card-title {
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #0D47A1;
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .pd-card-title i {
    color: #2196F3;
  }

  .pd-grid2 {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
  }

  .pd-stat-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid rgba(13, 71, 161, 0.06);
  }
  .pd-stat-row:last-child {
    border-bottom: none;
  }
  .pd-stat-label {
    font-size: 13px;
    color: #4A6D8C;
  }
  .pd-stat-value {
    font-weight: 600;
    font-size: 15px;
    color: #0D2436;
  }

  /* ── Teacher ── */
  .pd-teacher-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 16px;
    background: #FFFFFF;
    border-radius: 12px;
    border: 1px solid rgba(13, 71, 161, 0.06);
    margin-bottom: 10px;
  }
  .pd-teacher-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #0D47A1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 16px;
    color: #FFFFFF;
    flex-shrink: 0;
  }
  .pd-teacher-name {
    font-weight: 600;
    font-size: 14px;
    color: #0D2436;
  }
  .pd-teacher-subject {
    font-size: 12px;
    color: #4A6D8C;
  }
  .pd-contact-item {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #4A6D8C;
    padding: 3px 0;
  }
  .pd-contact-item i {
    width: 18px;
    color: #0D47A1;
  }
  .pd-btn-primary {
    width: 100%;
    margin-top: 12px;
    padding: 10px;
    background: #0D47A1;
    border: none;
    border-radius: 40px;
    color: #FFFFFF;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .pd-btn-primary:hover {
    background: #2196F3;
    box-shadow: 0 4px 16px rgba(13, 71, 161, 0.3);
    transform: translateY(-1px);
  }

  /* ── Period Chips ── */
  .pd-periods {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .pd-chip {
    padding: 10px 16px;
    background: #E3F2FD;
    border-radius: 12px;
    text-align: center;
    min-width: 90px;
    border: 1px solid rgba(13, 71, 161, 0.1);
    transition: all 0.3s ease;
  }
  .pd-chip:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(13, 71, 161, 0.08);
  }
  .pd-chip-period {
    font-size: 9px;
    font-weight: 500;
    color: #4A6D8C;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .pd-chip-subject {
    font-weight: 600;
    font-size: 13px;
    margin-top: 3px;
    color: #0D2436;
  }
  .pd-chip-teacher {
    font-size: 9px;
    color: #4A6D8C;
    margin-top: 3px;
  }

  /* ── Timetable ── */
  .pd-timetable-wrap {
    overflow-x: auto;
  }
  .pd-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    min-width: 750px;
  }
  .pd-th {
    padding: 10px 6px;
    background: #E3F2FD;
    color: #0D47A1;
    font-weight: 600;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #90CAF9;
    text-align: center;
  }
  .pd-td-day {
    padding: 10px 6px;
    font-weight: 600;
    color: #0D47A1;
    border-bottom: 1px solid rgba(13, 71, 161, 0.06);
    text-align: center;
  }
  .pd-td {
    padding: 6px 4px;
    border-bottom: 1px solid rgba(13, 71, 161, 0.06);
    text-align: center;
  }
  .pd-subject-pill {
    display: inline-block;
    padding: 5px 10px;
    border-radius: 8px;
    background: #E3F2FD;
    border: 1px solid rgba(13, 71, 161, 0.08);
    font-size: 11px;
    font-weight: 500;
    min-width: 70px;
  }
  .pd-subject-teacher {
    display: block;
    font-size: 8px;
    font-weight: 400;
    color: #4A6D8C;
    margin-top: 2px;
  }
  .pd-break-cell {
    font-size: 10px;
    color: #6E8CA6;
    font-style: italic;
  }

  /* ── Fees ── */
  .pd-fees-grid {
    display: grid;
    grid-template-columns: 1.2fr 1fr;
    gap: 20px;
  }
  .pd-fees-main {
    background: #E3F2FD;
    border-radius: 16px;
    padding: 24px;
    border: 1px solid rgba(13, 71, 161, 0.06);
  }
  .pd-fees-total-label {
    font-size: 11px;
    font-weight: 500;
    color: #4A6D8C;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .pd-fees-total-value {
    font-family: 'Playfair Display', serif;
    font-weight: 700;
    font-size: 36px;
    color: #0D2436;
    margin: 6px 0;
  }
  .pd-fees-split {
    display: flex;
    gap: 14px;
    margin-top: 14px;
  }
  .pd-fees-split-item {
    flex: 1;
    background: #FFFFFF;
    border-radius: 12px;
    padding: 14px;
    text-align: center;
    border: 1px solid rgba(13, 71, 161, 0.06);
  }
  .pd-fees-split-item .label {
    font-size: 10px;
    color: #4A6D8C;
  }
  .pd-fees-split-item .value {
    font-weight: 700;
    font-size: 18px;
    margin-top: 4px;
  }
  .pd-fees-split-item .value.green { color: #1A7A4A; }
  .pd-fees-split-item .value.red { color: #C62828; }

  .pd-term-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }
  .pd-term-card {
    background: #FFFFFF;
    border-radius: 12px;
    padding: 14px;
    border: 1px solid rgba(13, 71, 161, 0.06);
  }
  .pd-term-title {
    font-weight: 600;
    font-size: 11px;
    color: #0D47A1;
  }
  .pd-term-row {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    padding: 5px 0;
    border-bottom: 1px solid rgba(13, 71, 161, 0.04);
  }
  .pd-term-row:last-child { border-bottom: none; }
  .pd-progress-bar {
    height: 4px;
    background: #E3F2FD;
    border-radius: 4px;
    overflow: hidden;
    margin-top: 8px;
  }
  .pd-progress-fill {
    height: 100%;
    background: #0D47A1;
    border-radius: 4px;
    transition: width 0.6s ease;
  }

  /* ── Staff Grid ── */
  .pd-staff-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
  }
  .pd-staff-card {
    background: #E3F2FD;
    border-radius: 16px;
    padding: 18px 14px;
    text-align: center;
    border: 1px solid rgba(13, 71, 161, 0.06);
    transition: all 0.3s ease;
  }
  .pd-staff-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 16px rgba(13, 71, 161, 0.06);
  }
  .pd-staff-avatar {
    width: 56px;
    height: 56px;
    margin: 0 auto 10px;
    border-radius: 50%;
    background: #0D47A1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 18px;
    color: #FFFFFF;
  }
  .pd-staff-name {
    font-weight: 600;
    font-size: 14px;
    color: #0D2436;
  }
  .pd-staff-role {
    font-size: 11px;
    color: #4A6D8C;
    margin: 2px 0 6px;
  }
  .pd-staff-subject-tag {
    display: inline-block;
    padding: 2px 12px;
    border-radius: 40px;
    font-size: 10px;
    font-weight: 500;
    background: #FFFFFF;
    color: #0D47A1;
    margin: 6px 0;
  }
  .pd-staff-phone {
    font-size: 11px;
    color: #4A6D8C;
  }

  /* ── Messages ── */
  .pd-message-item {
    background: #E3F2FD;
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 10px;
    border: 1px solid rgba(13, 71, 161, 0.06);
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 14px;
  }
  .pd-message-content {
    flex: 1;
  }
  .pd-message-sender {
    font-weight: 600;
    font-size: 13px;
    color: #0D2436;
  }
  .pd-message-meta {
    font-size: 10px;
    color: #4A6D8C;
    margin: 3px 0 6px;
  }
  .pd-message-text {
    font-size: 13px;
    color: #1A2A3E;
  }
  .pd-message-time {
    font-size: 10px;
    color: #6E8CA6;
    margin-top: 4px;
  }
  .pd-delete-btn {
    background: transparent;
    border: 1px solid rgba(13, 71, 161, 0.08);
    padding: 5px 12px;
    border-radius: 40px;
    color: #C62828;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  .pd-delete-btn:hover {
    background: #C62828;
    color: #FFFFFF;
    border-color: #C62828;
  }

  /* ── Circulars ── */
  .pd-circular-item {
    background: #E3F2FD;
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 10px;
    border-left: 4px solid #0D47A1;
  }
  .pd-circular-title {
    font-weight: 600;
    font-size: 14px;
    color: #0D2436;
  }
  .pd-circular-date {
    font-size: 10px;
    color: #4A6D8C;
  }
  .pd-circular-body {
    font-size: 13px;
    color: #1A2A3E;
    margin: 6px 0;
  }
  .pd-circular-tag {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 40px;
    font-size: 9px;
    background: #FFFFFF;
    color: #0D47A1;
  }

  /* ── Report Card ── */
  .pd-report-wrapper {
    border: 1px solid rgba(13, 71, 161, 0.08);
    border-radius: 16px;
    padding: 28px;
    background: #FFFFFF;
  }
  .pd-report-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
    border-bottom: 2px solid #E3F2FD;
    flex-wrap: wrap;
    gap: 12px;
  }
  .pd-report-school {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .pd-report-school-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #0D47A1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #FFFFFF;
    font-size: 18px;
  }
  .pd-report-school-name {
    font-family: 'Playfair Display', serif;
    font-weight: 700;
    font-size: 18px;
    color: #0D2436;
  }
  .pd-report-school-sub {
    font-size: 11px;
    color: #4A6D8C;
  }
  .pd-report-student-info {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    background: #E3F2FD;
    padding: 14px 18px;
    border-radius: 12px;
    margin: 16px 0;
  }
  .pd-report-grid {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 6px;
    padding: 8px 14px;
    background: #E3F2FD;
    border-radius: 8px;
    font-weight: 600;
    font-size: 10px;
    text-transform: uppercase;
    color: #0D47A1;
    letter-spacing: 0.5px;
  }
  .pd-report-row {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 6px;
    padding: 8px 14px;
    border-bottom: 1px solid rgba(13, 71, 161, 0.04);
    font-size: 12px;
  }
  .pd-report-total {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 6px;
    padding: 10px 14px;
    background: #E3F2FD;
    font-weight: 700;
    border-top: 2px solid #90CAF9;
  }
  .pd-report-percent {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 6px;
    padding: 10px 14px;
    background: #E3F2FD;
    font-weight: 700;
    border-radius: 0 0 8px 8px;
  }
  .pd-report-signatures {
    display: flex;
    justify-content: space-between;
    margin-top: 32px;
    padding: 0 12px;
    gap: 32px;
    flex-wrap: wrap;
  }
  .pd-report-signature {
    text-align: center;
    flex: 1;
  }
  .pd-report-signature-line {
    height: 2px;
    border-bottom: 2px dashed #90CAF9;
    width: 160px;
    margin: 0 auto 6px;
  }
  .pd-report-signature-label {
    font-size: 10px;
    color: #4A6D8C;
    font-weight: 500;
  }

  /* ── Leave ── */
  .pd-leave-item {
    background: #E3F2FD;
    border: 1px solid rgba(13, 71, 161, 0.06);
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 10px;
  }
  .pd-leave-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 6px;
  }
  .pd-leave-type {
    font-weight: 600;
    font-size: 12px;
    color: #0D47A1;
  }
  .pd-leave-status {
    font-size: 11px;
    font-weight: 600;
    padding: 3px 12px;
    border-radius: 40px;
  }
  .pd-leave-status.pending {
    background: #FFF3E0;
    color: #E65100;
  }
  .pd-leave-status.approved {
    background: #E8F5E9;
    color: #2E7D32;
  }
  .pd-leave-status.rejected {
    background: #FFEBEE;
    color: #C62828;
  }
  .pd-leave-detail {
    font-size: 12px;
    color: #4A6D8C;
    margin-bottom: 4px;
  }
  .pd-leave-reason {
    font-size: 13px;
    color: #0D2436;
    margin-bottom: 4px;
  }
  .pd-leave-warden-remark {
    font-size: 11px;
    color: #4A6D8C;
    background: #FFFFFF;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid rgba(13, 71, 161, 0.04);
    margin-top: 6px;
  }

  /* ── Modal ── */
  .pd-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(13, 36, 54, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }
  .pd-modal {
    background: #FFFFFF;
    border-radius: 20px;
    padding: 28px;
    width: 90%;
    max-width: 440px;
    box-shadow: 0 20px 60px rgba(13, 36, 54, 0.2);
  }
  .pd-modal-title {
    font-family: 'Playfair Display', serif;
    font-weight: 700;
    font-size: 18px;
    color: #0D2436;
  }
  .pd-modal-sub {
    font-size: 13px;
    color: #4A6D8C;
    margin-bottom: 14px;
  }
  .pd-modal-textarea {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid rgba(13, 71, 161, 0.12);
    border-radius: 12px;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    resize: vertical;
    min-height: 90px;
    transition: border-color 0.3s ease;
  }
  .pd-modal-textarea:focus {
    outline: none;
    border-color: #0D47A1;
  }
  .pd-modal-actions {
    display: flex;
    gap: 10px;
    margin-top: 14px;
  }
  .pd-modal-btn {
    flex: 1;
    padding: 9px;
    border-radius: 40px;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: none;
  }
  .pd-modal-btn.primary {
    background: #0D47A1;
    color: #FFFFFF;
  }
  .pd-modal-btn.primary:hover {
    background: #2196F3;
    box-shadow: 0 4px 16px rgba(13, 71, 161, 0.3);
  }
  .pd-modal-btn.secondary {
    background: #E3F2FD;
    color: #4A6D8C;
  }
  .pd-modal-btn.secondary:hover {
    background: #90CAF9;
  }

  /* ── Loading ── */
  .pd-loading {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #E3F2FD;
  }
  .pd-loading-spinner {
    width: 44px;
    height: 44px;
    border: 3px solid #E3F2FD;
    border-top-color: #0D47A1;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .pd-loading-text {
    margin-top: 14px;
    font-weight: 500;
    color: #4A6D8C;
    letter-spacing: 1px;
  }

  .pd-empty {
    text-align: center;
    padding: 40px 20px;
    color: #4A6D8C;
  }
  .pd-empty i {
    font-size: 36px;
    color: #90CAF9;
    margin-bottom: 10px;
    display: block;
  }

  /* ── Responsive ── */
  @media (max-width: 1024px) {
    .pd-sidebar { width: 220px; }
    .pd-main { margin-left: 220px; padding: 20px; }
    .pd-hero { padding: 20px; }
    .pd-fees-grid { grid-template-columns: 1fr; }
    .pd-term-grid { grid-template-columns: repeat(3, 1fr); }
  }

  @media (max-width: 768px) {
    .pd-sidebar {
      width: 0;
      overflow: hidden;
      transform: translateX(-100%);
    }
    .pd-sidebar.open {
      width: 280px;
      transform: translateX(0);
    }
    .pd-main { margin-left: 0; padding: 16px; }
    .pd-hamburger { display: block; }
    .pd-hero { flex-direction: column; text-align: center; }
    .pd-hero-stats { margin-left: 0; justify-content: center; }
    .pd-hero-meta { justify-content: center; }
    .pd-term-grid { grid-template-columns: 1fr; }
    .pd-staff-grid { grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); }
    .pd-report-student-info { grid-template-columns: 1fr; }
    .pd-report-header { flex-direction: column; text-align: center; }
    .pd-report-signatures { flex-direction: column; align-items: center; }
    .pd-fees-split { flex-direction: column; }
    .pd-modal { padding: 20px; }
    .pd-grid2 { grid-template-columns: 1fr; }
    .pd-topbar-title { font-size: 18px; }
    .pd-content { padding: 16px; }
    .pd-topbar { flex-wrap: wrap; }
  }

  @media (max-width: 480px) {
    .pd-hero { padding: 16px; }
    .pd-hero-name { font-size: 18px; }
    .pd-hero-stat-value { font-size: 20px; }
    .pd-chip { min-width: 70px; padding: 8px 12px; }
    .pd-chip-subject { font-size: 11px; }
    .pd-staff-grid { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); }
    .pd-content { padding: 12px; }
    .pd-content-title { font-size: 16px; }
    .pd-table { font-size: 10px; min-width: 600px; }
    .pd-subject-pill { min-width: 60px; padding: 4px 8px; font-size: 10px; }
    .pd-sidebar.open { width: 260px; }
    .pd-topbar-date { font-size: 11px; padding: 4px 12px; }
  }

  @media print {
    .pd-sidebar, .pd-topbar, .pd-btn-primary, .pd-delete-btn,
    .pd-modal-overlay, .pd-hero { display: none !important; }
    .pd-main { margin-left: 0; padding: 0; }
    .pd-report-wrapper { border: none; padding: 0; }
    .pd-content { box-shadow: none; border: none; }
  }

  /* ── Sidebar Overlay ── */
  .pd-sidebar-overlay {
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(13, 36, 54, 0.4);
    z-index: 99;
  }
  .pd-sidebar-overlay.active {
    display: block;
  }
  @media (max-width: 768px) {
    .pd-sidebar-overlay.active {
      display: block;
    }
  }
`;
let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  const el = document.createElement('style');
  el.textContent = GLOBAL_CSS;
  document.head.appendChild(el);
  stylesInjected = true;
}

// ─────────────────────────────────────────────────────────────────────────────
//  MESSAGE MODAL (UNCHANGED)
// ─────────────────────────────────────────────────────────────────────────────
const MessageModal = ({
  teacher,
  parentName,
  className,
  studentName,
  apiBase,
  onClose,
  onSent
}) => {
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      let parentContact = '';
      try {
        parentContact = JSON.parse(localStorage.getItem('parentLogin') || '{}').parentContact || '';
      } catch (e) {}
      const payload = {
        className,
        studentName,
        parentName,
        parentContact,
        message: msg,
        senderType: 'parent'
      };
      if (teacher && (teacher.id || teacher._id)) payload.advisorStaffId = teacher.id || teacher._id;
      const res = await fetch(`${apiBase}/api/logbook-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed');
      if (onSent) {
        try {
          onSent();
        } catch (e) {}
      }
      setSent(true);
      setTimeout(onClose, 1400);
    } catch (err) {
      alert('Failed to send message. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return <div className="pd-modal-overlay" onClick={onClose}>
      <div className="pd-modal" onClick={e => e.stopPropagation()}>
        <div className="pd-modal-title">{teacher.name}</div>
        <div className="pd-modal-sub">{teacher.subject || teacher.dept} Teacher</div>
        {sent ? <div style={{
        textAlign: 'center',
        color: '#2E7D32',
        padding: '20px 0'
      }}>
            <i className="fas fa-check-circle" style={{
          fontSize: 32
        }}></i>
            <p style={{
          marginTop: 6
        }}>Message sent successfully</p>
          </div> : <>
            <textarea className="pd-modal-textarea" placeholder={`Write your message to ${teacher.name}…`} value={msg} onChange={e => setMsg(e.target.value)} rows={4} />
            <div className="pd-modal-actions">
              <button className="pd-modal-btn secondary" onClick={onClose}>Cancel</button>
              <button className="pd-modal-btn primary" onClick={send} disabled={busy}>
                {busy ? <i className="fas fa-spinner fa-pulse"></i> : <i className="fas fa-paper-plane"></i>}
                {busy ? ' Sending…' : ' Send'}
              </button>
            </div>
          </>}
      </div>
    </div>;
};

// ─────────────────────────────────────────────────────────────────────────────
//  HOSTEL RULES MODAL (view-only, for parents)
//  Reads live from the same GET /api/hostel/:hostelType/structure endpoint
//  that GirlsHostelManagement.js / BoysHostelManagement.js write to via the
//  Rooms & Washrooms tab, so any rule the Warden Adds/Deletes there shows up
//  here automatically — no separate storage, no add/delete for parents.
// ─────────────────────────────────────────────────────────────────────────────
const HostelRulesModal = ({
  hostelType,
  apiBase,
  onClose
}) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${apiBase}/api/hostel/${hostelType}/structure`);
        if (!res.ok) throw new Error("Failed to fetch");
        const data = await res.json();
        if (!cancelled) setRules(Array.isArray(data?.rules) ? data.rules : []);
      } catch (e) {
        if (!cancelled) setError(" Could not load hostel rules. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hostelType, apiBase]);
  return <div className="pd-modal-overlay" onClick={onClose}>
      <div className="pd-modal" onClick={e => e.stopPropagation()}>
        <div className="pd-modal-title">
          <i className="fas fa-thumbtack" style={{
          color: "#C62828",
          marginRight: 6
        }}></i>
          {hostelType} Hostel Rules
        </div>
        <div className="pd-modal-sub">Applies to the whole {hostelType} Hostel</div>

        {loading ? <div style={{
        textAlign: "center",
        padding: "20px 0",
        color: "#4A6D8C"
      }}>
            <i className="fas fa-spinner fa-pulse"></i> Loading rules…
          </div> : error ? <div style={{
        textAlign: "center",
        padding: "20px 0",
        color: "#C62828",
        fontSize: 13
      }}>{error}</div> : rules.length === 0 ? <div style={{
        textAlign: "center",
        padding: "20px 0",
        color: "#6E8CA6",
        fontStyle: "italic",
        fontSize: 13
      }}>
            No rules have been added yet.
          </div> : <div style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxHeight: 340,
        overflowY: "auto",
        margin: "14px 0"
      }}>
            {rules.map((rule, idx) => <div key={idx} style={{
          background: "#E3F2FD",
          borderRadius: 10,
          padding: "10px 14px",
          fontSize: 13,
          color: "#0D2436",
          lineHeight: 1.4
        }}>
                • {rule}
              </div>)}
          </div>}

        <div className="pd-modal-actions">
          <button className="pd-modal-btn primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>;
};

// ─────────────────────────────────────────────────────────────────────────────
//  LEAVE REQUEST PAGE
// ─────────────────────────────────────────────────────────────────────────────
const emptyLeaveForm = {
  type: "Leave",
  fromDate: "",
  toDate: "",
  fromTime: "",
  toTime: "",
  date: "",
  outTime: "",
  expectedInTime: "",
  destination: "",
  reason: ""
};
const LeaveRequestPage = ({
  studentData,
  parentName,
  apiBase,
  active
}) => {
  const [view, setView] = useState("list");
  const [form, setForm] = useState(emptyLeaveForm);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const gender = (studentData?.gender || "").trim().toLowerCase();
  const hostelType = gender === "female" ? "Girls" : gender === "male" ? "Boys" : "";
  const {
    dbClassName,
    section
  } = parseClassSection(studentData?.class || "");
  const fetchMyRequests = async () => {
    if (!hostelType || !studentData?.name) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/hostel/${hostelType}/requests`);
      if (res.ok) {
        const list = await res.json();
        const mine = (list || []).filter(r => (r.studentName || "").trim().toLowerCase() === (studentData?.name || "").trim().toLowerCase() && (r.className || "").trim().toLowerCase() === dbClassName.trim().toLowerCase() && (r.section || "").trim().toLowerCase() === section.trim().toLowerCase());
        setRequests(mine);
      }
    } catch (e) {/* keep last known list on failure */} finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!active) return;
    fetchMyRequests();
    const t = setInterval(fetchMyRequests, 20000);
    return () => clearInterval(t);
  }, [active, studentData?.name, studentData?.class, hostelType]);
  const submit = async () => {
    setError("");
    if (!form.reason.trim()) {
      setError("Please enter a reason.");
      return;
    }
    if (form.type === "Leave" && (!form.fromDate || !form.toDate)) {
      setError("Please fill both From Date and To Date.");
      return;
    }
    if (form.type === "Leave" && form.fromDate > form.toDate) {
      setError("From Date cannot be after To Date.");
      return;
    }
    if (form.type === "Permission" && (!form.date || !form.outTime)) {
      setError("Please fill Date and Out Time.");
      return;
    }
    if (!hostelType) {
      setError("Unable to determine the hostel (gender not on record). Please contact the school office.");
      return;
    }
    setBusy(true);
    try {
      let studentId = "";
      try {
        const stuRes = await fetch(`${apiBase}/api/hostel/${hostelType}/students`);
        if (stuRes.ok) {
          const list = await stuRes.json();
          const match = (list || []).find(s => (s.name || "").trim().toLowerCase() === (studentData?.name || "").trim().toLowerCase() && (s.className || "").trim().toLowerCase() === dbClassName.trim().toLowerCase() && (s.section || "").trim().toLowerCase() === section.trim().toLowerCase());
          if (match) studentId = match._id;
        }
      } catch (e) {/* non-fatal */}
      const res = await fetch(`${apiBase}/api/hostel/${hostelType}/requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          studentId,
          studentName: studentData?.name || "",
          className: dbClassName,
          section,
          parentName: parentName || "Parent",
          parentMobile: studentData?.fatherMobile || studentData?.motherMobile || "",
          type: form.type,
          reason: form.reason,
          fromDate: form.fromDate,
          toDate: form.toDate,
          fromTime: form.fromTime,
          toTime: form.toTime,
          date: form.date,
          outTime: form.outTime,
          expectedInTime: form.expectedInTime,
          destination: form.destination
        })
      });
      if (!res.ok) throw new Error("Failed");
      const saved = await res.json();
      setRequests(prev => [saved, ...prev]);
      setForm(emptyLeaveForm);
      setView("list");
    } catch (err) {
      setError(" Could not submit the request. Please check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };
  return <div>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
      marginBottom: 16
    }}>
        <div className="pd-content-title" style={{
        marginBottom: 0
      }}>
          <i className="fas fa-door-open"></i> Leave / Permission — For {studentData?.name}
        </div>
        <div style={{
        display: "flex",
        gap: 8,
        flexWrap: "wrap"
      }}>
          <button onClick={() => setShowRules(true)} style={{
          background: "#FFF3E0",
          color: "#E65100",
          border: "1px solid #FFCC80",
          padding: "8px 20px",
          borderRadius: 40,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer"
        }}>
            <i className="fas fa-thumbtack"></i> Hostel Rules
          </button>
          {view === "list" ? <button onClick={() => {
          setForm(emptyLeaveForm);
          setError("");
          setView("form");
        }} style={{
          background: "#0D47A1",
          color: "#FFFFFF",
          border: "none",
          padding: "8px 20px",
          borderRadius: 40,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer"
        }}>
              <i className="fas fa-plus"></i> New Request
            </button> : <button onClick={() => {
          setView("list");
          setError("");
        }} style={{
          background: "#E3F2FD",
          color: "#0D47A1",
          border: "1px solid #90CAF9",
          padding: "8px 20px",
          borderRadius: 40,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer"
        }}>
              <i className="fas fa-arrow-left"></i> Back to Requests
            </button>}
        </div>
      </div>

      {view === "form" ? <div>
          <div style={{
        display: "flex",
        gap: 8,
        marginBottom: 14
      }}>
            {["Leave", "Permission"].map(t => <button key={t} onClick={() => setForm(f => ({
          ...f,
          type: t
        }))} style={{
          flex: 1,
          padding: "9px 0",
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          border: `2px solid ${form.type === t ? '#0D47A1' : '#90CAF9'}`,
          background: form.type === t ? '#E3F2FD' : 'transparent',
          color: form.type === t ? '#0D47A1' : '#4A6D8C'
        }}>{t === "Leave" ? "Leave (going home)" : "Permission (same-day)"}</button>)}
          </div>

          {form.type === "Leave" ? <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 10,
        marginBottom: 14
      }}>
              <div><label style={{
            display: "block",
            fontSize: 11,
            fontWeight: 600,
            color: "#0D47A1",
            marginBottom: 4
          }}>FROM DATE</label>
                <input type="date" value={form.fromDate} onChange={e => setForm(f => ({
            ...f,
            fromDate: e.target.value
          }))} style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #90CAF9",
            fontSize: 13
          }} /></div>
              <div><label style={{
            display: "block",
            fontSize: 11,
            fontWeight: 600,
            color: "#0D47A1",
            marginBottom: 4
          }}>TO DATE</label>
                <input type="date" value={form.toDate} onChange={e => setForm(f => ({
            ...f,
            toDate: e.target.value
          }))} style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #90CAF9",
            fontSize: 13
          }} /></div>
              <div><label style={{
            display: "block",
            fontSize: 11,
            fontWeight: 600,
            color: "#0D47A1",
            marginBottom: 4
          }}>FROM TIME</label>
                <input type="time" value={form.fromTime} onChange={e => setForm(f => ({
            ...f,
            fromTime: e.target.value
          }))} style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #90CAF9",
            fontSize: 13
          }} /></div>
              <div><label style={{
            display: "block",
            fontSize: 11,
            fontWeight: 600,
            color: "#0D47A1",
            marginBottom: 4
          }}>TO TIME</label>
                <input type="time" value={form.toTime} onChange={e => setForm(f => ({
            ...f,
            toTime: e.target.value
          }))} style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #90CAF9",
            fontSize: 13
          }} /></div>
            </div> : <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 10,
        marginBottom: 14
      }}>
              <div><label style={{
            display: "block",
            fontSize: 11,
            fontWeight: 600,
            color: "#0D47A1",
            marginBottom: 4
          }}>DATE</label>
                <input type="date" value={form.date} onChange={e => setForm(f => ({
            ...f,
            date: e.target.value
          }))} style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #90CAF9",
            fontSize: 13
          }} /></div>
              <div><label style={{
            display: "block",
            fontSize: 11,
            fontWeight: 600,
            color: "#0D47A1",
            marginBottom: 4
          }}>DESTINATION</label>
                <input value={form.destination} onChange={e => setForm(f => ({
            ...f,
            destination: e.target.value
          }))} placeholder="e.g. Home, Clinic" style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #90CAF9",
            fontSize: 13
          }} /></div>
              <div><label style={{
            display: "block",
            fontSize: 11,
            fontWeight: 600,
            color: "#0D47A1",
            marginBottom: 4
          }}>OUT TIME</label>
                <input type="time" value={form.outTime} onChange={e => setForm(f => ({
            ...f,
            outTime: e.target.value
          }))} style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #90CAF9",
            fontSize: 13
          }} /></div>
              <div><label style={{
            display: "block",
            fontSize: 11,
            fontWeight: 600,
            color: "#0D47A1",
            marginBottom: 4
          }}>EXPECTED RETURN</label>
                <input type="time" value={form.expectedInTime} onChange={e => setForm(f => ({
            ...f,
            expectedInTime: e.target.value
          }))} style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #90CAF9",
            fontSize: 13
          }} /></div>
            </div>}

          <textarea className="pd-modal-textarea" placeholder="Reason for leave / permission…" value={form.reason} onChange={e => setForm(f => ({
        ...f,
        reason: e.target.value
      }))} rows={3} />

          {error && <div style={{
        background: "#FFEBEE",
        color: "#C62828",
        padding: "10px 14px",
        borderRadius: 8,
        fontSize: 13,
        marginTop: 10
      }}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {error}</div>}

          <button className="pd-modal-btn primary" onClick={submit} disabled={busy} style={{
        marginTop: 14,
        width: "100%"
      }}>
            {busy ? <i className="fas fa-spinner fa-pulse"></i> : <i className="fas fa-paper-plane"></i>}
            {busy ? ' Sending…' : ' Submit Request'}
          </button>
        </div> : <div>
          {loading && !requests.length ? <p style={{
        color: "#4A6D8C",
        fontSize: 13
      }}><i className="fas fa-spinner fa-pulse"></i> Loading your requests…</p> : !requests.length ? <div className="pd-empty"><i className="fas fa-door-open"></i> No leave / permission requests yet. Tap "New Request" to raise one.</div> : requests.map(r => {
        const statusClass = r.status === "Pending" ? "pending" : r.status === "Approved" ? "approved" : "rejected";
        return <div key={r._id} className="pd-leave-item">
                  <div className="pd-leave-header">
                    <span className="pd-leave-type">{r.type === "Leave" ? "Leave" : "Permission"}</span>
                    <span className={`pd-leave-status ${statusClass}`}>
                      <i className={`fas fa-${r.status === "Pending" ? "hourglass-half" : r.status === "Approved" ? "check-circle" : "times-circle"}`}></i>
                      {r.status === "Pending" ? " Waiting for approval" : ` ${r.status}`}
                    </span>
                  </div>
                  <div className="pd-leave-detail">
                    {r.type === "Leave" ? <><i className="far fa-calendar-alt"></i> {r.fromDate}{r.fromTime && ` · ${r.fromTime}`} <ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /> {r.toDate}{r.toTime && ` · ${r.toTime}`}</> : <><i className="far fa-calendar-alt"></i> {r.date} · Out: {r.outTime || "—"} · Return: {r.expectedInTime || "—"}{r.destination && ` · ${r.destination}`}</>}
                  </div>
                  <div className="pd-leave-reason">{r.reason}</div>
                  {r.wardenRemarks && <div className="pd-leave-warden-remark">
                      <i className="fas fa-comment-dots"></i> Warden: {r.wardenRemarks}
                    </div>}
                  <div style={{
            fontSize: 10,
            color: "#6E8CA6",
            marginTop: 6
          }}>
                    <i className="far fa-clock"></i> Requested {r.createdAt ? new Date(r.createdAt).toLocaleString() : ""}
                  </div>
                </div>;
      })}
        </div>}

      {showRules && hostelType && <HostelRulesModal hostelType={hostelType} apiBase={apiBase} onClose={() => setShowRules(false)} />}
    </div>;
};
const EXAMS_LIST = ['Mid Term 1', 'Quarterly', 'Mid Term 2', 'Half Yearly', 'Mid Term 3', 'Annual'];
const calcGrade = (marks, maxMarks) => {
  if (marks === null || marks === undefined) return '—';
  const pct = marks / (maxMarks || 100) * 100;
  if (pct >= 90) return 'A+';
  if (pct >= 80) return 'A';
  if (pct >= 70) return 'B';
  if (pct >= 60) return 'C';
  return 'D';
};
const gradeColor = grade => {
  if (!grade || grade === '—') return {
    bg: '#E3F2FD',
    color: '#4A6D8C'
  };
  if (grade === 'A+' || grade === 'A') return {
    bg: '#E8F5E9',
    color: '#2E7D32'
  };
  if (grade === 'B') return {
    bg: '#FFF3E0',
    color: '#E65100'
  };
  if (grade === 'C') return {
    bg: '#FFF3E0',
    color: '#E65100'
  };
  return {
    bg: '#FFEBEE',
    color: '#C62828'
  };
};

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN PARENT DASHBOARD — SIDEBAR LAYOUT WITH BLUE & WHITE THEME
// ─────────────────────────────────────────────────────────────────────────────
const ParentsDashboard = () => {
  const schoolName = useSchoolName();
  injectStyles();
  const [activeTab, setActiveTab] = useState("overview");
  const [msgTeacher, setMsgTeacher] = useState(null);
  const [parentMessages, setParentMessages] = useState([]);
  const [parentMsgsLoading, setParentMsgsLoading] = useState(false);
  const [deletingIds, setDeletingIds] = useState([]);
  const [studentData, setStudentData] = useState(null);
  const [parentName, setParentName] = useState("");
  const [classAdvisor, setClassAdvisor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allStaff, setAllStaff] = useState([]);
  const [circulars, setCirculars] = useState([]);
  const [circLoading, setCircLoading] = useState(false);
  const [attendancePct, setAttendancePct] = useState(null);
  const [attLoading, setAttLoading] = useState(false);
  const [dbTimetable, setDbTimetable] = useState(null);
  const [ttLoading, setTtLoading] = useState(false);
  const [ttError, setTtError] = useState("");
  const [fees, setFees] = useState(initialFees);
  const [feesLoading, setFeesLoading] = useState(false);
  const [feesError, setFeesError] = useState("");
  const [reportData, setReportData] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [activeReportExam, setActiveReportExam] = useState('Mid Term 1');
  const [feeTransactions, setFeeTransactions] = useState([]);
  const [termFees, setTermFees] = useState(initialTermFees);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const API_BASE = process.env.REACT_APP_API_URL || 'https://schoolcrm-fzcv.onrender.com';
  const navigate = useNavigate();
  useEffect(() => {
    const parentLogin = localStorage.getItem("parentLogin");
    if (!parentLogin) {
      navigate("/login");
      return;
    }
    const parentInfo = JSON.parse(parentLogin);
    const className = parentInfo.className;
    setParentName(parentInfo.parentName || "Parent");
    setStudentData({
      name: parentInfo.studentName,
      class: className,
      rollNo: "Loading...",
      dob: "Loading...",
      photo: null,
      studentId: parentInfo.studentId || null
    });
    if (parentInfo.studentId) fetchStudentProfile(parentInfo.studentId, parentInfo.studentName, className);else setStudentData({
      name: parentInfo.studentName,
      class: className,
      rollNo: "Not Assigned",
      dob: "Not Available",
      photo: null,
      studentId: null
    });
    fetchClassAdvisor(className);
    fetchAllStaff();
    fetchTimetable(className);
    if (parentInfo.studentId) fetchFees(parentInfo.studentId);
    fetchAttendance(className, parentInfo.studentId);
    fetchCirculars(className);
    fetchParentMessages(parentInfo.parentName);
    if (parentInfo.studentId) fetchStudentReport(parentInfo.studentId, className);
  }, [navigate]);
  const fetchStudentProfile = async (studentId, studentName, className) => {
    try {
      const res = await fetch(`${API_BASE}/api/admissions/${studentId}`);
      if (!res.ok) {
        setStudentData({
          name: studentName,
          class: className,
          rollNo: "Not Assigned",
          dob: "Not Available",
          photo: null,
          studentId
        });
        return;
      }
      const adm = await res.json();
      let formattedDob = "Not Available";
      if (adm.dob) {
        try {
          const d = new Date(adm.dob);
          if (!isNaN(d.getTime())) formattedDob = d.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "long",
            year: "numeric"
          });else formattedDob = adm.dob;
        } catch {
          formattedDob = adm.dob;
        }
      }
      const rollNo = adm.rollNo && adm.rollNo.trim() !== "" ? adm.rollNo : "Not Assigned";
      const photo = adm.photo && adm.photo.trim() !== "" ? adm.photo : null;
      setStudentData({
        name: adm.studentName || studentName,
        class: className,
        rollNo,
        dob: formattedDob,
        photo,
        studentId,
        fatherName: adm.fatherName || "",
        gender: adm.gender || "",
        fatherMobile: adm.fatherMobile || "",
        motherMobile: adm.motherMobile || ""
      });
    } catch {
      setStudentData({
        name: studentName,
        class: className,
        rollNo: "Not Assigned",
        dob: "Not Available",
        photo: null,
        studentId
      });
    }
  };
  const fetchAttendance = async (fullClassName, studentId) => {
    if (!fullClassName) return;
    const {
      dbClassName,
      section
    } = parseClassSection(fullClassName);
    if (!dbClassName || !section) return;
    setAttLoading(true);
    try {
      const params = new URLSearchParams({
        className: dbClassName,
        section,
        startDate: academicYearStart(),
        endDate: todayIST()
      });
      const res = await fetch(`${API_BASE}/api/attendance/regular/history?${params}`);
      if (!res.ok) {
        setAttendancePct(null);
        return;
      }
      const json = await res.json();
      const docs = Array.isArray(json.data) ? json.data : [];
      let present = 0,
        absent = 0,
        late = 0;
      docs.forEach(doc => {
        (doc.entries || []).forEach(entry => {
          if (studentId && String(entry.studentId) !== String(studentId)) return;
          if (entry.status === "P") present++;else if (entry.status === "A") absent++;else if (entry.status === "L") late++;
        });
      });
      const total = present + absent + late;
      setAttendancePct(total === 0 ? null : Math.round((present + late) / total * 100));
    } catch {
      setAttendancePct(null);
    } finally {
      setAttLoading(false);
    }
  };
  const attDisplay = attLoading ? "…" : attendancePct !== null ? `${attendancePct}%` : "N/A";
  const attColor = !attLoading && attendancePct !== null && attendancePct < 75 ? attendancePct < 50 ? "#C62828" : "#E65100" : "#0D47A1";
  const fetchParentMessages = async pName => {
    let name = pName || parentName;
    let storedContact = '';
    if (!name) {
      try {
        const pl = JSON.parse(localStorage.getItem('parentLogin') || '{}');
        name = pl.parentName || '';
        storedContact = pl.parentContact || '';
      } catch (e) {}
    } else {
      try {
        storedContact = JSON.parse(localStorage.getItem('parentLogin') || '{}').parentContact || '';
      } catch (e) {}
    }
    name = (name || '').trim();
    if (!name) return;
    setParentMsgsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/logbook-messages/parent/${encodeURIComponent(name)}`);
      if (!res.ok) {
        setParentMessages([]);
        return;
      }
      const j = await res.json();
      const arr = Array.isArray(j) ? j : [];
      const lowerName = name.toLowerCase();
      setParentMessages(arr.filter(pm => {
        if (!pm) return false;
        const pn = (pm.parentName || '').toLowerCase();
        const pc = pm.parentContact || '';
        return pn === lowerName || pn.includes(lowerName) || storedContact && pc && pc.includes(storedContact);
      }));
    } catch {
      setParentMessages([]);
    } finally {
      setParentMsgsLoading(false);
    }
  };
  const deleteParentMessage = async id => {
    if (!window.confirm('Delete this message?')) return;
    setDeletingIds(prev => [...prev, id]);
    try {
      const res = await fetch(`${API_BASE}/api/logbook-messages/${id}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('delete failed');
      setParentMessages(prev => prev.filter(m => m._id !== id));
    } catch (err) {
      alert('Failed to delete: ' + (err.message || 'Unknown'));
    } finally {
      setDeletingIds(prev => prev.filter(x => x !== id));
    }
  };
  const fetchCirculars = async className => {
    setCircLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/circulars`);
      if (!res.ok) throw new Error('failed');
      const j = await res.json();
      if (!j.success) {
        setCirculars([]);
        return;
      }
      const baseClass = (className || '').split('-')[0];
      setCirculars((j.circulars || []).filter(c => !c.isDraft && (c.recipientsMeta?.allParents || Array.isArray(c.recipientsMeta?.classes) && c.recipientsMeta.classes.includes(baseClass))));
    } catch {
      setCirculars([]);
    } finally {
      setCircLoading(false);
    }
  };
  const fetchFees = async studentId => {
    setFeesLoading(true);
    setFeesError("");
    try {
      const feeSummaryRes = await fetch(`${API_BASE}/api/fees/full/${studentId}`);
      if (!feeSummaryRes.ok) throw new Error('Failed to fetch fee summary');
      const feeSummary = await feeSummaryRes.json();
      const feesRes = await fetch(`${API_BASE}/api/fees/student/${studentId}`);
      let tx = [];
      if (feesRes.ok) tx = await feesRes.json();
      const totalFee = typeof feeSummary.totalFee === 'number' && feeSummary.totalFee > 0 ? feeSummary.totalFee : 12000;
      let termFeesData;
      if (feeSummary.termFees && feeSummary.termFees.T1) {
        const nf = tf => ({
          due: tf.due || 0,
          paid: tf.paid || 0,
          pending: typeof tf.pending === 'number' ? tf.pending : Math.max(0, (tf.due || 0) - (tf.paid || 0)),
          components: tf.components || {
            schoolFee: tf.due || 0,
            vanFee: 0,
            bookFee: 0,
            tuitionFee: 0,
            others: []
          },
          componentsPaid: tf.componentsPaid || {
            schoolFee: 0,
            vanFee: 0,
            bookFee: 0,
            tuitionFee: 0,
            others: []
          }
        });
        termFeesData = {
          T1: nf(feeSummary.termFees.T1),
          T2: nf(feeSummary.termFees.T2),
          T3: nf(feeSummary.termFees.T3)
        };
      } else {
        const t1 = Math.ceil(totalFee / 3);
        const t2 = Math.floor((totalFee - t1) / 2);
        const t3 = totalFee - t1 - t2;
        termFeesData = {
          T1: {
            due: t1,
            paid: 0,
            pending: t1,
            components: {
              schoolFee: t1,
              vanFee: 0,
              bookFee: 0,
              tuitionFee: 0,
              others: []
            },
            componentsPaid: {
              schoolFee: 0,
              vanFee: 0,
              bookFee: 0,
              tuitionFee: 0,
              others: []
            }
          },
          T2: {
            due: t2,
            paid: 0,
            pending: t2,
            components: {
              schoolFee: t2,
              vanFee: 0,
              bookFee: 0,
              tuitionFee: 0,
              others: []
            },
            componentsPaid: {
              schoolFee: 0,
              vanFee: 0,
              bookFee: 0,
              tuitionFee: 0,
              others: []
            }
          },
          T3: {
            due: t3,
            paid: 0,
            pending: t3,
            components: {
              schoolFee: t3,
              vanFee: 0,
              bookFee: 0,
              tuitionFee: 0,
              others: []
            },
            componentsPaid: {
              schoolFee: 0,
              vanFee: 0,
              bookFee: 0,
              tuitionFee: 0,
              others: []
            }
          }
        };
      }
      const total = totalFee;
      const paid = typeof feeSummary.paid === 'number' ? feeSummary.paid : (termFeesData.T1.paid || 0) + (termFeesData.T2.paid || 0) + (termFeesData.T3.paid || 0);
      const balance = typeof feeSummary.pending === 'number' ? feeSummary.pending : Math.max(0, total - paid);
      setFees({
        total,
        paid,
        balance,
        history: (tx || []).map(f => ({
          term: f.term || 'OTHER',
          amount: f.amount || 0,
          date: f.createdAt ? new Date(f.createdAt).toLocaleDateString() : '',
          status: 'Paid'
        }))
      });
      setTermFees(termFeesData);
      setFeeTransactions(tx || []);
    } catch (err) {
      console.error('Failed to load fees:', err);
      setFeesError('Failed to load fees.');
      setFees(initialFees);
      setTermFees(initialTermFees);
      setFeeTransactions([]);
    } finally {
      setFeesLoading(false);
    }
  };
  const fetchTimetable = async className => {
    setTtLoading(true);
    setTtError("");
    try {
      const res = await fetch(`${API_BASE}/api/timetables/${encodeURIComponent(className)}`);
      if (res.status === 404) {
        setDbTimetable(null);
        setTtError("No timetable assigned for this class yet.");
      } else if (!res.ok) throw new Error("Server error");else {
        const data = await res.json();
        setDbTimetable(data.timetable || null);
      }
    } catch {
      setTtError("Failed to load timetable.");
    } finally {
      setTtLoading(false);
    }
  };
  const fetchStudentReport = async (studentId, className) => {
    const sId = studentId || studentData?.studentId;
    const clsName = className || studentData?.class;
    if (!sId) return;
    setReportLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/marks/student/${sId}?className=${encodeURIComponent(clsName)}`);
      if (!res.ok) throw new Error('Failed to fetch marks');
      const data = await res.json();
      setReportData(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setReportData([]);
    } finally {
      setReportLoading(false);
    }
  };
  const fetchClassAdvisor = async className => {
    try {
      const advisorRes = await fetch(`${API_BASE}/api/class-advisors/${encodeURIComponent(className)}`);
      if (!advisorRes.ok) {
        setClassAdvisor(null);
        return;
      }
      const advisorData = await advisorRes.json();
      if (!advisorData?.staffId) {
        setClassAdvisor(null);
        return;
      }
      const staffId = typeof advisorData.staffId === "object" && advisorData.staffId._id ? advisorData.staffId._id : advisorData.staffId;
      const staffRes = await fetch(`${API_BASE}/api/staff/${staffId}`);
      if (!staffRes.ok) {
        const f = advisorData.staffId;
        setClassAdvisor({
          id: f._id || f.id || null,
          name: f.name || "Class Advisor",
          subject: f.dept || "Teacher",
          role: f.role || `${f.dept || "Class"} Teacher`,
          phone: f.phone || "Not available",
          email: f.email || "Not available",
          qualification: f.qual || "",
          experience: f.exp || "",
          dept: f.dept || ""
        });
        return;
      }
      const staff = await staffRes.json();
      setClassAdvisor({
        id: staff._id || staff.id,
        name: staff.name,
        subject: staff.dept,
        role: staff.role || `${staff.dept} Teacher`,
        phone: staff.phone,
        email: staff.email,
        qualification: staff.qual,
        experience: staff.exp,
        dept: staff.dept
      });
    } catch {
      setClassAdvisor(null);
    }
  };
  const fetchAllStaff = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/staff`);
      if (res.ok) {
        const staff = await res.json();
        setAllStaff(staff.map(s => ({
          id: s._id || s.id,
          name: s.name,
          subject: s.dept,
          role: s.role || `${s.dept} Teacher`,
          phone: s.phone,
          email: s.email,
          dept: s.dept
        })));
      }
    } catch {} finally {
      setLoading(false);
    }
  };
  const handleLogout = () => {
    localStorage.removeItem("parentLogin");
    navigate("/parentlogin");
  };
  const paidPct = fees.total > 0 ? Math.round(fees.paid / fees.total * 100) : 0;
  const getInitial = () => parentName && parentName !== "Parent" ? parentName.charAt(0).toUpperCase() : "P";
  const classStaffList = useMemo(() => {
    if (!dbTimetable || !allStaff.length) return [];
    const teacherNames = new Set();
    Object.keys(dbTimetable).forEach(day => {
      const dayData = dbTimetable[day] || {};
      PERIODS.forEach(p => {
        const t = (dayData[p.id]?.teacher || '').toString().trim();
        if (t) teacherNames.add(t);
      });
    });
    const names = Array.from(teacherNames).map(n => n.toLowerCase());
    const matchesName = (tName, staffName) => {
      if (!tName || !staffName) return false;
      const t = tName.toLowerCase();
      const s = staffName.toLowerCase();
      return t === s || t.includes(s) || s.includes(t);
    };
    return allStaff.filter(s => names.some(n => matchesName(n, s.name)));
  }, [dbTimetable, allStaff]);
  const subjects = useMemo(() => {
    const subjSet = new Set();
    if (dbTimetable) {
      Object.keys(dbTimetable).forEach(day => {
        const dayData = dbTimetable[day] || {};
        PERIODS.forEach(p => {
          const s = (dayData[p.id]?.subject || '').toString().trim();
          if (s && !p.isBreak) subjSet.add(s);
        });
      });
    }
    reportData.forEach(r => {
      if (r.subject) subjSet.add(r.subject);
    });
    return Array.from(subjSet);
  }, [dbTimetable, reportData]);
  const getTodayPeriods = () => {
    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const todayName = dayNames[new Date().getDay()];
    const day = dbTimetable?.[todayName] ? todayName : "Monday";
    if (!dbTimetable?.[day]) return {
      day,
      periods: []
    };
    const dayData = dbTimetable[day];
    return {
      day,
      periods: PERIODS.filter(p => !p.isBreak).map(p => ({
        label: p.label,
        time: p.time,
        subject: dayData[p.id]?.subject || "",
        teacher: dayData[p.id]?.teacher || ""
      }))
    };
  };
  if (loading) {
    return <div className="pd-loading">
        <div className="pd-loading-spinner"></div>
        <div className="pd-loading-text">Loading Dashboard</div>
      </div>;
  }
  const todayInfo = getTodayPeriods();
  const SIDEBAR_ITEMS = [{
    id: "overview",
    label: "Overview",
    icon: "fas fa-th-large"
  }, {
    id: "timetable",
    label: "Timetable",
    icon: "fas fa-calendar-alt"
  }, {
    id: "fees",
    label: "Fees",
    icon: "fas fa-coins"
  }, {
    id: "staff",
    label: "Staff",
    icon: "fas fa-users"
  }, {
    id: "circulars",
    label: "Circulars",
    icon: "fas fa-bullhorn"
  }, {
    id: "messages",
    label: "Messages",
    icon: "fas fa-envelope"
  }, {
    id: "report",
    label: "Report",
    icon: "fas fa-file-alt"
  }, {
    id: "leave",
    label: "Leave / Permission",
    icon: "fas fa-door-open"
  }];
  const getTabTitle = () => {
    const item = SIDEBAR_ITEMS.find(t => t.id === activeTab);
    return item ? item.label : "Overview";
  };
  return <div className="pd-app">
      {/* Sidebar Overlay */}
      <div className={`pd-sidebar-overlay ${sidebarOpen ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}></div>

      {/* ── SIDEBAR ── */}
      <aside className={`pd-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="pd-sidebar-brand">
          <div className="pd-sidebar-logo">
            <div className="pd-sidebar-icon"><i className="fas fa-graduation-cap"></i></div>
            <div>
              <div className="pd-sidebar-title">{schoolName}</div>
              <div className="pd-sidebar-sub">Parent Portal</div>
            </div>
          </div>
        </div>

        <div className="pd-sidebar-user">
          <div className="pd-sidebar-avatar">{getInitial()}</div>
          <div>
            <div className="pd-sidebar-user-name">{parentName}</div>
            <div className="pd-sidebar-user-role">Parent · {studentData?.class}</div>
          </div>
        </div>

        <nav className="pd-sidebar-nav">
          <div className="pd-sidebar-nav-label">Menu</div>
          {SIDEBAR_ITEMS.map(item => <button key={item.id} className={`pd-sidebar-item ${activeTab === item.id ? 'active' : ''}`} onClick={() => {
          setActiveTab(item.id);
          setSidebarOpen(false);
          if (item.id === 'messages') fetchParentMessages(parentName);
          if (item.id === 'report') fetchStudentReport(studentData?.studentId, studentData?.class);
        }}>
              <i className={item.icon}></i>
              {item.label}
              {item.id === 'messages' && parentMessages.length > 0 && <span className="badge">{parentMessages.length}</span>}
            </button>)}
        </nav>

        <div className="pd-sidebar-footer">
          <button className="pd-sidebar-logout" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt"></i> Logout
          </button>
        </div>
      </aside>

      {/* ── MAIN CONTENT ── */}
      <main className="pd-main">
        {/* Top Bar */}
        <div className="pd-topbar">
          <div className="pd-topbar-left">
            <button className="pd-hamburger" onClick={() => setSidebarOpen(true)}>
              <i className="fas fa-bars"></i>
            </button>
            <div className="pd-topbar-title">
              {getTabTitle()} <span>·</span> {studentData?.name}
            </div>
          </div>
          <div className="pd-topbar-date">
            <i className="far fa-calendar-alt"></i> {new Date().toLocaleDateString('en-US', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          })}
          </div>
        </div>

        {/* Hero Card */}
        {studentData && <div className="pd-hero">
            <div className="pd-hero-photo">
              {studentData.photo ? <img src={studentData.photo} alt={studentData.name} style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "50%"
          }} /> : <i className="fas fa-user-graduate"></i>}
            </div>
            <div className="pd-hero-info">
              <div className="pd-hero-name">{studentData.name}</div>
              <div className="pd-hero-meta">
                <span><i className="fas fa-graduation-cap"></i> {studentData.class}</span>
                <span><i className="fas fa-id-card"></i> Roll No: {studentData.rollNo}</span>
                <span><i className="fas fa-cake-candles"></i> DOB: {studentData.dob}</span>
              </div>
            </div>
            <div className="pd-hero-stats">
              <div className="pd-hero-stat">
                <div className="pd-hero-stat-value" style={{
              color: attColor
            }}>{attDisplay}</div>
                <div className="pd-hero-stat-label">Attendance</div>
              </div>
              <div className="pd-hero-stat">
                <div className="pd-hero-stat-value" style={{
              color: fees.balance > 0 ? '#C62828' : '#2E7D32'
            }}>
                  ₹{fees.balance.toLocaleString()}
                </div>
                <div className="pd-hero-stat-label">Balance Due</div>
              </div>
            </div>
          </div>}

        {/* Content */}
        <div className="pd-content">
          {/* ══ OVERVIEW ══ */}
          {activeTab === "overview" && <div>
              <div className="pd-grid2">
                <div className="pd-card">
                  <div className="pd-card-title"><i className="fas fa-chart-pie"></i> Quick Stats</div>
                  {[{
                label: "Attendance",
                value: attDisplay,
                color: attColor
              }, {
                label: "Fees Paid",
                value: `₹${fees.paid.toLocaleString()}`,
                color: "#2E7D32"
              }, {
                label: "Balance Due",
                value: `₹${fees.balance.toLocaleString()}`,
                color: "#C62828"
              }, {
                label: "Class",
                value: studentData?.class || "N/A",
                color: "#0D47A1"
              }].map(item => <div key={item.label} className="pd-stat-row">
                      <span className="pd-stat-label">{item.label}</span>
                      <span className="pd-stat-value" style={{
                  color: item.color
                }}>{item.value}</span>
                    </div>)}
                </div>

                <div className="pd-card">
                  <div className="pd-card-title"><i className="fas fa-user-tie"></i> Class Advisor</div>
                  {classAdvisor ? <>
                      <div className="pd-teacher-card">
                        <div className="pd-teacher-avatar">{classAdvisor.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}</div>
                        <div>
                          <div className="pd-teacher-name">{classAdvisor.name}</div>
                          <div className="pd-teacher-subject">{classAdvisor.subject}</div>
                        </div>
                      </div>
                      <div className="pd-contact-item"><i className="fas fa-phone-alt"></i> {classAdvisor.phone}</div>
                      <div className="pd-contact-item"><i className="fas fa-envelope"></i> {classAdvisor.email}</div>
                      {classAdvisor.qualification && <div className="pd-contact-item"><i className="fas fa-graduation-cap"></i> {classAdvisor.qualification}</div>}
                      {classAdvisor.experience && <div className="pd-contact-item"><i className="fas fa-star"></i> {classAdvisor.experience} yrs</div>}
                      <button className="pd-btn-primary" onClick={() => setMsgTeacher(classAdvisor)}>
                        <i className="fas fa-paper-plane"></i> Send Message
                      </button>
                    </> : <div className="pd-empty" style={{
                padding: "20px 0"
              }}>
                      <i className="fas fa-user-tie" style={{
                  fontSize: 32
                }}></i>
                      <p style={{
                  marginTop: 6,
                  fontSize: 13
                }}>No class advisor assigned yet</p>
                      <p style={{
                  fontSize: 11,
                  color: "#6E8CA6"
                }}>Please contact school administration.</p>
                    </div>}
                </div>
              </div>

              <div className="pd-card" style={{
            marginTop: 20
          }}>
                <div className="pd-card-title"><i className="fas fa-clock"></i> Today's Schedule — {todayInfo.day}</div>
                {ttLoading ? <p style={{
              color: "#4A6D8C",
              fontSize: 13
            }}><i className="fas fa-spinner fa-pulse"></i> Loading schedule…</p> : ttError || !dbTimetable ? <p style={{
              color: "#C62828",
              fontSize: 13
            }}><i className="fas fa-exclamation-circle"></i> {ttError || "No timetable available."}</p> : <div className="pd-periods">
                    {todayInfo.periods.map((p, i) => {
                const col = subjectColors[p.subject];
                return <div key={i} className="pd-chip" style={{
                  borderLeft: `3px solid ${col || '#0D47A1'}`
                }}>
                          <div className="pd-chip-period">{p.label}</div>
                          <div className="pd-chip-subject" style={{
                    color: col || '#0D2436'
                  }}>{p.subject || <span style={{
                      color: "#6E8CA6"
                    }}>—</span>}</div>
                          {p.teacher && <div className="pd-chip-teacher"><i className="fas fa-user"></i> {p.teacher}</div>}
                        </div>;
              })}
                  </div>}
              </div>
            </div>}

          {/* ══ TIMETABLE ══ */}
          {activeTab === "timetable" && <div>
              <div className="pd-content-title"><i className="fas fa-calendar-week"></i> Weekly Timetable — {studentData?.class}</div>
              {ttLoading && <p style={{
            color: "#4A6D8C",
            fontSize: 13
          }}><i className="fas fa-spinner fa-pulse"></i> Loading timetable…</p>}
              {!ttLoading && ttError && <div className="pd-empty"><i className="fas fa-exclamation-circle"></i> {ttError}</div>}
              {!ttLoading && !ttError && dbTimetable && <div className="pd-timetable-wrap">
                  <table className="pd-table">
                    <thead>
                      <tr>
                        <th className="pd-th">Day</th>
                        {PERIODS.map(p => <th key={p.id} className="pd-th" style={{
                    minWidth: p.isBreak ? 70 : 85
                  }}>
                            {p.label}
                            <div style={{
                      fontSize: 9,
                      fontWeight: 400,
                      color: "#6E8CA6"
                    }}>{p.time}</div>
                          </th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {DAYS.map((day, di) => {
                  const dayData = dbTimetable[day] || {};
                  return <tr key={day}>
                            <td className="pd-td-day">{day.slice(0, 3)}</td>
                            {PERIODS.map(p => {
                      if (p.isBreak) return <td key={p.id} className="pd-td" style={{
                        background: "#E3F2FD"
                      }}>
                                  <span className="pd-break-cell"><i className="fas fa-coffee"></i> {p.label}</span>
                                </td>;
                      const cell = dayData[p.id] || {};
                      const col = subjectColors[cell.subject];
                      return <td key={p.id} className="pd-td">
                                  {cell.subject ? <div className="pd-subject-pill" style={{
                          borderColor: col || '#90CAF9'
                        }}>
                                      {cell.subject}
                                      {cell.teacher && <span className="pd-subject-teacher">{cell.teacher}</span>}
                                    </div> : <span style={{
                          fontSize: 10,
                          color: "#90CAF9"
                        }}>—</span>}
                                </td>;
                    })}
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>}
              {!ttLoading && !ttError && !dbTimetable && <div className="pd-empty"><i className="fas fa-calendar-times"></i> No timetable assigned yet.</div>}
            </div>}

          {/* ══ FEES ══ */}
          {activeTab === "fees" && <div>
              <div className="pd-content-title"><i className="fas fa-coins"></i> Fee Details</div>
              {feesLoading && <div style={{
            color: "#4A6D8C",
            fontSize: 13,
            marginBottom: 12
          }}><i className="fas fa-spinner fa-pulse"></i> Loading fees…</div>}
              {feesError && <div style={{
            color: "#C62828",
            fontSize: 13,
            marginBottom: 12
          }}><i className="fas fa-exclamation-circle"></i> {feesError}</div>}
              <div className="pd-fees-grid">
                <div className="pd-fees-main">
                  <div className="pd-fees-total-label">Total Fees</div>
                  <div className="pd-fees-total-value">₹{fees.total.toLocaleString()}</div>
                  <div className="pd-fees-split">
                    <div className="pd-fees-split-item">
                      <div className="label">Paid</div>
                      <div className="value green">₹{fees.paid.toLocaleString()}</div>
                    </div>
                    <div className="pd-fees-split-item">
                      <div className="label">Pending</div>
                      <div className="value red">₹{fees.balance.toLocaleString()}</div>
                    </div>
                  </div>
                </div>
                <div className="pd-term-grid">
                  {['T1', 'T2', 'T3'].map((k, idx) => <div key={k} className="pd-term-card">
                      <div className="pd-term-title">Term {idx + 1}</div>
                      <div className="pd-term-row"><span>Due</span><span>₹{(termFees[k].due || 0).toLocaleString()}</span></div>
                      <div className="pd-term-row"><span>Paid</span><span style={{
                    color: "#2E7D32"
                  }}>₹{(termFees[k].paid || 0).toLocaleString()}</span></div>
                      <div className="pd-term-row"><span>Pending</span><span style={{
                    color: "#C62828"
                  }}>₹{(termFees[k].pending || 0).toLocaleString()}</span></div>
                      <div className="pd-progress-bar">
                        <div className="pd-progress-fill" style={{
                    width: `${(termFees[k].due || 0) > 0 ? Math.round((termFees[k].paid || 0) / (termFees[k].due || 1) * 100) : 0}%`
                  }}></div>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Fee Components */}
              <div className="pd-card" style={{
            marginTop: 20
          }}>
                <div className="pd-card-title"><i className="fas fa-list-ul"></i> Fee Components</div>
                <div className="pd-grid2">
                  {['T1', 'T2', 'T3'].map((k, idx) => {
                const tf = termFees[k] || {};
                const comps = tf.components || {};
                const cp = tf.componentsPaid || {};
                const rows = [{
                  label: 'School Fee',
                  due: comps.schoolFee || 0,
                  paid: cp.schoolFee || 0
                }, {
                  label: 'Tuition Fee',
                  due: comps.tuitionFee || 0,
                  paid: cp.tuitionFee || 0
                }, {
                  label: 'Van Fee',
                  due: comps.vanFee || 0,
                  paid: cp.vanFee || 0
                }, {
                  label: 'Book Fee',
                  due: comps.bookFee || 0,
                  paid: cp.bookFee || 0
                }];
                (Array.isArray(comps.others) ? comps.others : []).forEach(o => {
                  const pe = (Array.isArray(cp.others) ? cp.others : []).find(x => x.label === o.label);
                  rows.push({
                    label: o.label,
                    due: o.amount || 0,
                    paid: pe ? pe.amount || 0 : 0
                  });
                });
                const visibleRows = rows.filter(r => r.due > 0);
                return <div key={k} style={{
                  background: "#E3F2FD",
                  borderRadius: 12,
                  padding: 14,
                  border: "1px solid rgba(13, 71, 161, 0.06)"
                }}>
                        <div style={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: "#0D47A1",
                    marginBottom: 8
                  }}>Term {idx + 1}</div>
                        {visibleRows.length === 0 ? <div style={{
                    fontSize: 11,
                    color: "#4A6D8C"
                  }}>No components set.</div> : visibleRows.map((r, i) => {
                    const pending = Math.max(0, r.due - r.paid);
                    return <div key={i} style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "5px 0",
                      borderBottom: i < visibleRows.length - 1 ? "1px solid rgba(13, 71, 161, 0.06)" : "none",
                      fontSize: 11
                    }}>
                              <span style={{
                        color: "#0D2436"
                      }}>{r.label}</span>
                              <span>
                                <span style={{
                          color: "#2E7D32",
                          fontWeight: 600
                        }}>₹{r.paid.toLocaleString()}</span>
                                <span style={{
                          color: "#4A6D8C"
                        }}> / ₹{r.due.toLocaleString()}</span>
                                {pending > 0 && <span style={{
                          color: "#C62828",
                          fontWeight: 600,
                          marginLeft: 4
                        }}>
                                    (Pending ₹{pending.toLocaleString()})
                                  </span>}
                              </span>
                            </div>;
                  })}
                      </div>;
              })}
                </div>
              </div>

              {/* Payment History */}
              <div className="pd-card" style={{
            marginTop: 20
          }}>
                <div style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 10,
              alignItems: "center"
            }}>
                  <span className="pd-card-title" style={{
                marginBottom: 0
              }}><i className="fas fa-history"></i> Payment History</span>
                  <span style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#0D47A1"
              }}>{paidPct}% Paid</span>
                </div>
                <div className="pd-progress-bar" style={{
              marginBottom: 16
            }}>
                  <div className="pd-progress-fill" style={{
                width: `${paidPct}%`
              }}></div>
                </div>

                {(() => {
              const allTxAsc = [...feeTransactions].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
              const overallRemainingById = {};
              let runOverall = 0;
              for (const t of allTxAsc) {
                runOverall += t.amount || 0;
                overallRemainingById[t._id] = Math.max(0, (fees.total || 0) - runOverall);
              }
              const termRemainingById = {
                T1: {},
                T2: {},
                T3: {},
                OTHER: {}
              };
              for (const k of ['T1', 'T2', 'T3']) {
                const txs = [...feeTransactions].filter(t => (t.term || 'OTHER') === k).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                let run = 0;
                const due = termFees[k]?.due || 0;
                for (const t of txs) {
                  run += t.amount || 0;
                  termRemainingById[k][t._id] = Math.max(0, due - run);
                }
              }
              const otherTxs = [...feeTransactions].filter(t => (t.term || 'OTHER') === 'OTHER').sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
              let runOther = 0;
              for (const t of otherTxs) {
                runOther += t.amount || 0;
                termRemainingById.OTHER[t._id] = Math.max(0, (fees.total || 0) - runOther);
              }
              return ['T1', 'T2', 'T3', 'OTHER'].map((k, idx) => {
                const txs = [...feeTransactions].filter(t => (t.term || 'OTHER') === k).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                if (!txs.length) return null;
                return <div key={k} style={{
                  marginTop: 14
                }}>
                        <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 6
                  }}>
                          <div style={{
                      fontWeight: 600,
                      fontSize: 12,
                      color: "#0D2436"
                    }}>{k === 'OTHER' ? 'Other Payments' : `Term ${idx + 1}`}</div>
                          <div style={{
                      fontSize: 11,
                      color: "#4A6D8C"
                    }}>Remaining: ₹{(k === 'OTHER' ? termFees.T1.pending + termFees.T2.pending + termFees.T3.pending : termFees[k]?.pending || 0).toLocaleString()}</div>
                        </div>
                        <div style={{
                    background: "#E3F2FD",
                    borderRadius: 10,
                    overflow: "hidden",
                    border: "1px solid rgba(13, 71, 161, 0.06)"
                  }}>
                          {txs.map(t => <div key={t._id} style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      borderBottom: "1px solid rgba(13, 71, 161, 0.04)"
                    }}>
                              <div>
                                <div style={{
                          fontWeight: 600,
                          fontSize: 11,
                          color: "#0D2436"
                        }}><i className="fas fa-credit-card"></i> {(t.method || 'PAYMENT').toUpperCase()}</div>
                                <div style={{
                          fontSize: 9,
                          color: "#4A6D8C",
                          marginTop: 1
                        }}><i className="far fa-calendar-alt"></i> {new Date(t.createdAt).toLocaleString()}</div>
                                {t.note && <div style={{
                          fontSize: 9,
                          color: "#6E8CA6",
                          marginTop: 1
                        }}>{t.note}</div>}
                              </div>
                              <div style={{
                        textAlign: "right"
                      }}>
                                <div style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#2E7D32"
                        }}>₹{(t.amount || 0).toLocaleString()}</div>
                                <div style={{
                          fontSize: 9,
                          color: "#4A6D8C"
                        }}>Remaining: ₹{(k === 'OTHER' ? termRemainingById.OTHER[t._id] ?? overallRemainingById[t._id] : termRemainingById[k][t._id] ?? 0).toLocaleString()}</div>
                              </div>
                            </div>)}
                        </div>
                      </div>;
              });
            })()}
              </div>
            </div>}

          {/* ══ STAFF ══ */}
          {activeTab === "staff" && <div>
              <div className="pd-content-title"><i className="fas fa-users"></i> Subject Teachers</div>
              <div className="pd-staff-grid">
                {(() => {
              const staffToShow = dbTimetable ? classStaffList : allStaff;
              if (!staffToShow.length) return <div className="pd-empty"><i className="fas fa-users-slash"></i> No staff records found.</div>;
              return staffToShow.map((teacher, i) => <div key={i} className="pd-staff-card">
                      <div className="pd-staff-avatar">{teacher.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}</div>
                      <div className="pd-staff-name">{teacher.name}</div>
                      <div className="pd-staff-role">{teacher.role}</div>
                      <span className="pd-staff-subject-tag" style={{
                  background: subjectColors[teacher.subject] ? `${subjectColors[teacher.subject]}10` : "#FFFFFF",
                  color: subjectColors[teacher.subject] || "#0D47A1"
                }}>
                        {teacher.subject}
                      </span>
                      <div className="pd-staff-phone" style={{
                  margin: "4px 0"
                }}><i className="fas fa-phone-alt"></i> {teacher.phone}</div>
                      <button className="pd-btn-primary" style={{
                  marginTop: 6
                }} onClick={() => setMsgTeacher(teacher)}>
                        <i className="fas fa-paper-plane"></i> Message
                      </button>
                    </div>);
            })()}
              </div>
            </div>}

          {/* ══ CIRCULARS ══ */}
          {activeTab === "circulars" && <div>
              <div className="pd-content-title"><i className="fas fa-bullhorn"></i> Circulars</div>
              {circLoading ? <p style={{
            color: "#4A6D8C",
            fontSize: 13
          }}><i className="fas fa-spinner fa-pulse"></i> Loading circulars…</p> : !circulars.length ? <div className="pd-empty"><i className="fas fa-newspaper"></i> No circulars found.</div> : circulars.map(c => <div key={c._id} className="pd-circular-item">
                    <div style={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 6
            }}>
                      <div className="pd-circular-title">{c.title}</div>
                      <div className="pd-circular-date"><i className="far fa-calendar-alt"></i> {new Date(c.createdAt).toLocaleDateString()}</div>
                    </div>
                    <div className="pd-circular-body">{c.message}</div>
                    <span className="pd-circular-tag">
                      <i className="fas fa-tag"></i> {c.recipientsMeta?.allParents ? 'All Parents' : (c.recipientsMeta?.classes || []).join(', ')}
                    </span>
                  </div>)}
            </div>}

          {/* ══ MESSAGES ══ */}
          {activeTab === "messages" && <div>
              <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
            flexWrap: "wrap",
            gap: 8
          }}>
                <div className="pd-content-title" style={{
              marginBottom: 0
            }}><i className="fas fa-envelope-open-text"></i> Sent Messages</div>
                <div style={{
              color: "#4A6D8C",
              fontSize: 13
            }}>{parentMsgsLoading ? "loading…" : `${parentMessages.length} messages`}</div>
              </div>
              {parentMsgsLoading && <div style={{
            color: "#4A6D8C",
            fontSize: 13
          }}><i className="fas fa-spinner fa-pulse"></i> Loading messages…</div>}
              {!parentMsgsLoading && !parentMessages.length && <div className="pd-empty"><i className="fas fa-inbox"></i> No sent messages yet.</div>}
              {!parentMsgsLoading && parentMessages.map(pm => <div key={pm._id} className="pd-message-item">
                  <div className="pd-message-content">
                    <div className="pd-message-sender">{pm.studentName || pm.parentName}</div>
                    <div className="pd-message-meta">
                      <span className="pd-circular-tag" style={{
                  background: "#FFFFFF"
                }}>
                        {pm.senderType === 'staff' ? `Staff · ${pm.senderName || 'Teacher'}` : 'Parent'}
                      </span>
                      <span style={{
                  marginLeft: 6
                }}><i className="fas fa-graduation-cap"></i> {pm.className}</span>
                    </div>
                    <div className="pd-message-text">{pm.message}</div>
                    <div className="pd-message-time"><i className="far fa-clock"></i> {new Date(pm.createdAt).toLocaleString()}</div>
                  </div>
                  <button className="pd-delete-btn" disabled={deletingIds.includes(pm._id)} onClick={() => deleteParentMessage(pm._id)}>
                    <i className="fas fa-trash-alt"></i> Delete
                  </button>
                </div>)}
            </div>}

          {/* ══ REPORT ══ */}
          {activeTab === "report" && <div>
              <div className="pd-content-title"><i className="fas fa-file-alt"></i> Academic Report Card</div>
              {reportLoading ? <p style={{
            color: "#4A6D8C",
            fontSize: 13
          }}><i className="fas fa-spinner fa-pulse"></i> Loading report card…</p> : !subjects.length ? <div className="pd-empty"><i className="fas fa-file-excel"></i> No subjects or marks records found.</div> : <div className="pd-report-wrapper">
                  <div className="pd-report-header">
                    <div className="pd-report-school">
                      <div className="pd-report-school-icon"><i className="fas fa-graduation-cap"></i></div>
                      <div>
                        <div className="pd-report-school-name">{schoolName}</div>
                        <div className="pd-report-school-sub">Academic Progress Report</div>
                      </div>
                    </div>
                    <div style={{
                textAlign: "right",
                fontSize: 12,
                color: "#4A6D8C"
              }}>
                      <div><strong>Class:</strong> {studentData?.class}</div>
                      <div><strong>Year:</strong> {new Date().getFullYear()}</div>
                    </div>
                  </div>

                  {/* Exam Selection */}
                  <div style={{
              display: "flex",
              gap: 6,
              overflowX: "auto",
              margin: "14px 0",
              paddingBottom: 6
            }}>
                    {EXAMS_LIST.map(exam => {
                const isActive = activeReportExam === exam;
                return <button key={exam} onClick={() => setActiveReportExam(exam)} style={{
                  padding: "5px 14px",
                  borderRadius: 40,
                  border: `2px solid ${isActive ? '#0D47A1' : '#90CAF9'}`,
                  background: isActive ? '#E3F2FD' : 'transparent',
                  color: isActive ? '#0D47A1' : '#4A6D8C',
                  fontWeight: 600,
                  fontSize: 11,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.3s ease"
                }}>
                          {exam}
                        </button>;
              })}
                  </div>

                  <div className="pd-report-student-info">
                    <div>
                      <div><strong>Student:</strong> {studentData?.name}</div>
                      <div><strong>Roll No:</strong> {studentData?.rollNo}</div>
                    </div>
                    <div style={{
                textAlign: "right"
              }}>
                      <div><strong>Exam:</strong> {activeReportExam}</div>
                      <div><strong>Advisor:</strong> {classAdvisor?.name || '—'}</div>
                    </div>
                  </div>

                  <div className="pd-report-grid">
                    <div>Subject</div>
                    <div style={{
                textAlign: "center"
              }}>Marks</div>
                    <div style={{
                textAlign: "center"
              }}>Max</div>
                    <div style={{
                textAlign: "center"
              }}>Grade</div>
                  </div>

                  {subjects.map((sub, idx) => {
              const record = reportData.find(r => r.subject === sub && r.exam === activeReportExam);
              const entry = record?.entry;
              const m = entry && entry.marks !== null && entry.marks !== undefined ? entry.marks : null;
              const mm = entry ? entry.maxMarks || 100 : 100;
              const grade = m !== null ? calcGrade(m, mm) : '—';
              const colors = gradeColor(grade);
              return <div key={sub} className="pd-report-row">
                        <div style={{
                  fontWeight: 600,
                  color: "#0D2436"
                }}>{sub}</div>
                        <div style={{
                  textAlign: "center",
                  fontWeight: 700,
                  color: m !== null ? "#0D2436" : "#90CAF9"
                }}>{m !== null ? m : '—'}</div>
                        <div style={{
                  textAlign: "center",
                  color: "#4A6D8C"
                }}>{mm}</div>
                        <div style={{
                  textAlign: "center"
                }}>
                          <span style={{
                    display: "inline-block",
                    padding: "2px 10px",
                    borderRadius: 40,
                    fontWeight: 700,
                    fontSize: 10,
                    background: colors.bg,
                    color: colors.color
                  }}>{grade}</span>
                        </div>
                      </div>;
            })}

                  {(() => {
              let obtainedSum = 0,
                maxSum = 0,
                hasMarks = false;
              subjects.forEach(sub => {
                const record = reportData.find(r => r.subject === sub && r.exam === activeReportExam);
                const entry = record?.entry;
                if (entry && entry.marks !== null && entry.marks !== undefined) {
                  obtainedSum += entry.marks;
                  maxSum += entry.maxMarks || 100;
                  hasMarks = true;
                }
              });
              if (!hasMarks) {
                return <div style={{
                  padding: 16,
                  textAlign: "center",
                  color: "#6E8CA6",
                  fontStyle: "italic",
                  fontSize: 12
                }}>No marks recorded for this exam yet.</div>;
              }
              const pct = maxSum > 0 ? Math.round(obtainedSum / maxSum * 100) : 0;
              const totalGrade = calcGrade(obtainedSum, maxSum);
              const totalGradeColors = gradeColor(totalGrade);
              return <>
                        <div className="pd-report-total">
                          <div>Total</div>
                          <div style={{
                    textAlign: "center",
                    color: "#0D47A1"
                  }}>{obtainedSum}</div>
                          <div style={{
                    textAlign: "center",
                    color: "#4A6D8C"
                  }}>{maxSum}</div>
                          <div style={{
                    textAlign: "center"
                  }}>
                            <span style={{
                      display: "inline-block",
                      padding: "2px 10px",
                      borderRadius: 40,
                      fontWeight: 700,
                      fontSize: 10,
                      background: totalGradeColors.bg,
                      color: totalGradeColors.color
                    }}>{totalGrade}</span>
                          </div>
                        </div>
                        <div className="pd-report-percent">
                          <div>Percentage</div>
                          <div style={{
                    textAlign: "center",
                    color: pct >= 50 ? "#2E7D32" : "#C62828"
                  }}>{pct}%</div>
                          <div style={{
                    textAlign: "center"
                  }}>—</div>
                          <div style={{
                    textAlign: "center"
                  }}>—</div>
                        </div>
                      </>;
            })()}

                  <div style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 20
            }}>
                    <button onClick={() => window.print()} style={{
                padding: "8px 20px",
                background: "#0D47A1",
                color: "#FFFFFF",
                border: "none",
                borderRadius: 40,
                fontWeight: 600,
                fontSize: 12,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                transition: "all 0.3s ease"
              }}>
                      <i className="fas fa-print"></i> Print
                    </button>
                  </div>
                </div>}
            </div>}

          {/* ══ LEAVE ══ */}
          {activeTab === "leave" && <LeaveRequestPage studentData={studentData} parentName={parentName} apiBase={API_BASE} active={activeTab === "leave"} />}
        </div>
      </main>

      {/* Message Modal */}
      {msgTeacher && <MessageModal teacher={msgTeacher} parentName={parentName} className={studentData?.class} studentName={studentData?.name} apiBase={API_BASE} onClose={() => setMsgTeacher(null)} onSent={() => fetchParentMessages(parentName)} />}
    </div>;
};
export default ParentsDashboard;